import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface MultiSelectOption {
  id: string;
  label: string;
}

export interface MultiSelectGroup {
  id: string;
  parent: MultiSelectOption;
  children: MultiSelectOption[];
}

interface MultiSelectBaseProps {
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
}

type MultiSelectProps = MultiSelectBaseProps &
  (
    | { options: MultiSelectOption[]; groups?: never }
    | { options?: never; groups: MultiSelectGroup[] }
  );

function flattenGroups(groups: MultiSelectGroup[]): MultiSelectOption[] {
  return groups.flatMap((group) => [group.parent, ...group.children]);
}

function filterGroups(
  groups: MultiSelectGroup[],
  query: string,
): MultiSelectGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;

  return groups.flatMap((group) => {
    const parentMatches = group.parent.label.toLowerCase().includes(q);
    const matchingChildren = group.children.filter((child) =>
      child.label.toLowerCase().includes(q),
    );

    if (parentMatches) return [group];
    if (matchingChildren.length > 0) {
      return [{ ...group, children: matchingChildren }];
    }
    return [];
  });
}

export function MultiSelect({
  options,
  groups,
  value,
  onChange,
  placeholder = "Sélectionner…",
  searchPlaceholder = "Rechercher…",
  disabled = false,
  emptyMessage = "Aucun résultat.",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const allOptions = useMemo(
    () => (groups ? flattenGroups(groups) : options),
    [groups, options],
  );
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || groups) return [];
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [groups, options, search]);

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    return filterGroups(groups, search);
  }, [groups, search]);

  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 0);
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  function toggle(id: string) {
    const next = new Set(selectedSet);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(Array.from(next));
  }

  function remove(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  }

  const selectedOptions = allOptions.filter((opt) => selectedSet.has(opt.id));
  const hasResults = groups
    ? filteredGroups.length > 0
    : (search.trim() ? filteredOptions : options).length > 0;

  function renderOption(
    opt: MultiSelectOption,
    className = "",
  ) {
    const isSelected = selectedSet.has(opt.id);
    return (
      <li
        key={opt.id}
        role="option"
        aria-selected={isSelected}
        onClick={() => toggle(opt.id)}
        className={`flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm transition-colors hover:bg-neutral-50 ${className} ${
          isSelected ? "text-whart-text" : "text-neutral-600"
        }`}
      >
        <span>{opt.label}</span>
        {isSelected ? (
          <span className="text-xs text-whart-dark">✓</span>
        ) : null}
      </li>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        className={`min-h-9 w-full wireframe-border bg-whart-bg px-2 py-1.5 text-sm flex flex-wrap gap-1.5 items-center cursor-pointer transition-colors ${
          disabled
            ? "pointer-events-none opacity-50"
            : "hover:border-neutral-400"
        } ${isOpen ? "border-whart-dark" : ""}`}
      >
        {selectedOptions.length === 0 ? (
          <span className="text-neutral-400 px-1">{placeholder}</span>
        ) : (
          selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className="inline-flex items-center gap-1 bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 text-xs text-whart-text"
            >
              {opt.label}
              <button
                type="button"
                aria-label={`Retirer ${opt.label}`}
                disabled={disabled}
                onClick={(e) => remove(opt.id, e)}
                className="text-neutral-400 hover:text-whart-urgent leading-none"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {isOpen ? (
        <div className="absolute z-20 mt-0.5 w-full wireframe-border bg-whart-bg shadow-sm">
          <div className="border-b border-black/10 px-2 py-1.5">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent text-sm text-whart-text outline-none placeholder:text-neutral-400"
            />
          </div>
          <ul
            role="listbox"
            aria-multiselectable="true"
            className="max-h-48 overflow-y-auto"
          >
            {!hasResults ? (
              <li className="px-3 py-2 text-xs text-neutral-400">
                {emptyMessage}
              </li>
            ) : groups ? (
              filteredGroups.map((group) => (
                <Fragment key={group.id}>
                  {renderOption(
                    group.parent,
                    "bg-neutral-50 font-semibold text-whart-text",
                  )}
                  {group.children.map((child) =>
                    renderOption(child, "pl-6"),
                  )}
                </Fragment>
              ))
            ) : (
              (search.trim() ? filteredOptions : options).map((opt) =>
                renderOption(opt),
              )
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
